import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import {
  X,
  Save,
  AlertCircle,
  Loader2,
  Users,
  Clock,
  Info,
  Check,
  ArrowLeftRight,
  ArrowUpDown,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import type { EventCheckoutSessionAttendee } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutSessionDetails/types";
import { RootState } from "@/app/rootReducer";
import { AttendeeWaitlistRequest } from "@/modules/eventRegistration/features/EventRegistration/api/updateEventCheckoutAttendeeWaitlist/types";
import {
  getEventCheckoutSessionDetailsAction,
  updateEventCheckoutAttendeeWaitlistAction,
} from "@/modules/eventRegistration/features/EventRegistration/slices/eventCheckoutSlice";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  allAttendees: EventCheckoutSessionAttendee[];
  eventName: string;
  nonWaitlistedAttendeesInCheckout: number;
  minimumOffWaitlist?: number;
  checkoutSessionUuid: string;
}

const brandColors = {
  primary: "#B21E34",
  secondary: "#17084A",
  primaryLight: "#B21E3410",
  secondaryLight: "#17084A10",
  primaryHover: "#8A1828",
  secondaryHover: "#0F0633",
};

function WaitlistModal({
  isOpen,
  onClose,
  allAttendees,
  eventName,
  nonWaitlistedAttendeesInCheckout,
  minimumOffWaitlist = 1,
  checkoutSessionUuid,
}: WaitlistModalProps) {
  const dispatch = useDispatch();
  const loadingUpdateWaitlist = useSelector(
    (state: RootState) => state.eventCheckout.loadingUpdateWaitlist,
  );
  const updateWaitlistError = useSelector(
    (state: RootState) => state.eventCheckout.updateWaitlistError,
  );

  const [localAttendees, setLocalAttendees] = useState<
    EventCheckoutSessionAttendee[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedConfirmed, setSelectedConfirmed] = useState<number | null>(
    null,
  );
  const [selectedWaitlist, setSelectedWaitlist] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // Changed to lg breakpoint
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const selectedAttendees = allAttendees.filter((a) => a.isSelected);
    setLocalAttendees(selectedAttendees);
    setHasChanges(false);
    setSelectedConfirmed(null);
    setSelectedWaitlist(null);
  }, [allAttendees, isOpen]);

  const confirmedAttendees = localAttendees.filter((a) => !a.isWaitlist);
  const waitlistedAttendees = localAttendees.filter((a) => a.isWaitlist);
  const confirmedCount = confirmedAttendees.length;

  function handleConfirmedClick(attendeeId: number) {
    if (selectedWaitlist) {
      // If a waitlist attendee is selected, perform swap
      handleSwap(attendeeId, selectedWaitlist);
    } else {
      // Otherwise, select this confirmed attendee
      setSelectedConfirmed(
        selectedConfirmed === attendeeId ? null : attendeeId,
      );
    }
  }

  function handleWaitlistClick(attendeeId: number) {
    if (selectedConfirmed) {
      // If a confirmed attendee is selected, perform swap
      handleSwap(selectedConfirmed, attendeeId);
    } else {
      // Otherwise, select this waitlist attendee
      setSelectedWaitlist(selectedWaitlist === attendeeId ? null : attendeeId);
    }
  }

  function handleSwap(confirmedId: number, waitlistId: number) {
    setLocalAttendees((prev) => {
      return prev.map((attendee) => {
        if (attendee.id === confirmedId) {
          return { ...attendee, isWaitlist: true };
        } else if (attendee.id === waitlistId) {
          return { ...attendee, isWaitlist: false };
        }
        return attendee;
      });
    });
    setHasChanges(true);
    setSelectedConfirmed(null);
    setSelectedWaitlist(null);
  }

  function handleSave() {
    const attendeeUpdates: AttendeeWaitlistRequest[] = localAttendees.map(
      (attendee) => ({
        attendeeId: attendee.id,
        isWaitlist: attendee.isWaitlist,
      }),
    );
    dispatch(
      updateEventCheckoutAttendeeWaitlistAction(
        checkoutSessionUuid,
        { attendees: attendeeUpdates },
        () => {
          dispatch(getEventCheckoutSessionDetailsAction(checkoutSessionUuid));
          onClose();
        },
      ),
    );
  }

  const modalStyles = isMobile
    ? {
        content: {
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          transform: "none",
          borderRadius: "0",
          padding: "0",
          background: "white",
          width: "100%",
          height: "100%",
          maxWidth: "none",
          maxHeight: "none",
          display: "flex",
          flexDirection: "column" as const,
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.75)",
          zIndex: 9999,
        },
      }
    : {
        content: {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "20px",
          padding: "0",
          background: "white",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          width: "calc(100% - 48px)",
          maxWidth: "1200px",
          height: "90vh",
          maxHeight: "900px",
          display: "flex",
          flexDirection: "column" as const,
          overflow: "hidden",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.75)",
          zIndex: 9999,
        },
      };

  return (
    <Modal
      contentLabel="Manage Attendee List"
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
    >
      {/* Fixed Header */}
      <div className="px-6 py-4 md:px-8 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                Manage Attendee List
              </h2>
              <p className="text-sm text-gray-600">{eventName}</p>
            </div>
            {/* Inline Stats */}
            <div className="hidden md:flex items-center gap-3 ml-8">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                <Users
                  className="w-4 h-4"
                  style={{ color: brandColors.primary }}
                />
                <span className="text-sm font-medium">
                  {confirmedCount}/{nonWaitlistedAttendeesInCheckout} Confirmed
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium">
                  {waitlistedAttendees.length} Waitlisted
                </span>
              </div>
            </div>
          </div>
          <button
            className="p-2 rounded-xl hover:bg-gray-100 transition-all duration-200"
            onClick={onClose}
            type="button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Mobile Stats */}
        <div className="flex md:hidden items-center gap-3 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200 flex-1">
            <Users className="w-4 h-4" style={{ color: brandColors.primary }} />
            <span className="text-sm font-medium">
              {confirmedCount}/{nonWaitlistedAttendeesInCheckout}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200 flex-1">
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium">
              {waitlistedAttendees.length} Waitlisted
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Instructions */}
        <div className="px-6 md:px-8 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <Info
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: brandColors.secondary }}
            />
            <div className="text-sm">
              <p
                className="font-semibold"
                style={{ color: brandColors.secondary }}
              >
                How to swap attendees:
              </p>
              <ul className="mt-2 space-y-1 text-gray-600">
                <li>• Click on any attendee to select them</li>
                <li>
                  • Then click on someone from the other list to swap their
                  positions
                </li>
                <li>
                  • You must maintain exactly {nonWaitlistedAttendeesInCheckout}{" "}
                  confirmed seat
                  {nonWaitlistedAttendeesInCheckout !== 1 ? "s" : ""}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {updateWaitlistError && (
          <div
            className="mx-6 mt-4 md:mx-8 p-3 rounded-xl border-2"
            style={{
              backgroundColor: `${brandColors.primary}08`,
              borderColor: `${brandColors.primary}20`,
            }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: brandColors.primary }}
              />
              <div>
                <p
                  className="text-sm font-semibold"
                  style={{ color: brandColors.primary }}
                >
                  Error: {updateWaitlistError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Attendee Lists */}
        <div className="p-6 md:p-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-stretch">
            {/* Confirmed Column */}
            <div className="flex-1 flex flex-col">
              <div
                className="rounded-t-xl border-2 border-b-0 px-4 py-2.5"
                style={{
                  backgroundColor: "#059669" + "08",
                  borderColor: "#059669" + "20",
                }}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-4 h-4" style={{ color: "#059669" }} />
                    Confirmed Seats
                  </h3>
                  <span
                    className="px-2.5 py-0.5 text-sm font-bold rounded-full"
                    style={{
                      backgroundColor: "#059669" + "15",
                      color: "#059669",
                    }}
                  >
                    {confirmedCount}
                  </span>
                </div>
              </div>
              <div
                className="flex-1 border-2 border-t-0 rounded-b-xl p-3"
                style={{ borderColor: "#059669" + "20" }}
              >
                {confirmedAttendees.length === 0 ? (
                  <div className="py-16 flex items-center justify-center text-gray-400">
                    <p>No confirmed attendees</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {confirmedAttendees.map((attendee) => (
                      <div
                        className={`
                          p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 relative
                          ${
                            selectedConfirmed === attendee.id
                              ? "shadow-lg"
                              : "hover:shadow-md hover:border-gray-300"
                          }
                        `}
                        key={attendee.id}
                        onClick={() => handleConfirmedClick(attendee.id)}
                        style={{
                          borderColor:
                            selectedConfirmed === attendee.id
                              ? brandColors.primary
                              : "#e5e7eb",
                          backgroundColor:
                            selectedConfirmed === attendee.id
                              ? brandColors.primaryLight
                              : "white",
                          boxShadow:
                            selectedConfirmed === attendee.id
                              ? `0 0 0 3px ${brandColors.primary}30, 0 10px 15px -3px rgba(0, 0, 0, 0.1)`
                              : undefined,
                        }}
                      >
                        {selectedConfirmed === attendee.id && (
                          <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md">
                            <Check
                              className="w-4 h-4"
                              style={{ color: brandColors.primary }}
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {attendee.firstName} {attendee.lastName}
                            </h5>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {attendee.email || "No email provided"}
                            </p>
                            {attendee.specialRequests && (
                              <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                                <span className="font-medium">Note:</span>{" "}
                                {attendee.specialRequests}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Center Swap Indicator - Desktop */}
            <div className="hidden lg:flex items-center justify-center px-4">
              <div className="flex flex-col items-center gap-3">
                <ArrowLeftRight
                  className={`w-8 h-8 ${selectedConfirmed || selectedWaitlist ? "text-gray-400 animate-pulse" : "text-gray-300"}`}
                />
                {(selectedConfirmed || selectedWaitlist) && (
                  <p className="text-xs text-gray-500 text-center max-w-[100px]">
                    Select someone from the other list to swap
                  </p>
                )}
              </div>
            </div>

            {/* Center Swap Indicator - Mobile */}
            <div className="flex lg:hidden items-center justify-center py-4">
              <div className="flex flex-col items-center gap-3">
                <ArrowUpDown
                  className={`w-8 h-8 ${selectedConfirmed || selectedWaitlist ? "text-gray-400 animate-pulse" : "text-gray-300"}`}
                />
                {(selectedConfirmed || selectedWaitlist) && (
                  <p className="text-xs text-gray-500 text-center max-w-[150px]">
                    Select someone from the other list to swap
                  </p>
                )}
              </div>
            </div>

            {/* Waitlist Column */}
            <div className="flex-1 flex flex-col">
              <div className="rounded-t-xl border-2 border-b-0 px-4 py-2.5 bg-amber-50 border-amber-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    Waitlist
                  </h3>
                  <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-sm font-bold rounded-full">
                    {waitlistedAttendees.length}
                  </span>
                </div>
              </div>
              <div className="flex-1 border-2 border-t-0 border-amber-200 rounded-b-xl p-3">
                {waitlistedAttendees.length === 0 ? (
                  <div className="py-16 flex items-center justify-center text-gray-400">
                    <p>No attendees on waitlist</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {waitlistedAttendees.map((attendee) => (
                      <div
                        className={`
                          p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 relative
                          ${
                            selectedWaitlist === attendee.id
                              ? "shadow-lg"
                              : "hover:shadow-md hover:border-gray-300"
                          }
                        `}
                        key={attendee.id}
                        onClick={() => handleWaitlistClick(attendee.id)}
                        style={{
                          borderColor:
                            selectedWaitlist === attendee.id
                              ? brandColors.primary
                              : "#e5e7eb",
                          backgroundColor:
                            selectedWaitlist === attendee.id
                              ? brandColors.primaryLight
                              : "white",
                          boxShadow:
                            selectedWaitlist === attendee.id
                              ? `0 0 0 3px ${brandColors.primary}30, 0 10px 15px -3px rgba(0, 0, 0, 0.1)`
                              : undefined,
                        }}
                      >
                        {selectedWaitlist === attendee.id && (
                          <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md">
                            <Check
                              className="w-4 h-4"
                              style={{ color: brandColors.primary }}
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {attendee.firstName} {attendee.lastName}
                            </h5>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {attendee.email || "No email provided"}
                            </p>
                            {attendee.specialRequests && (
                              <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                                <span className="font-medium">Note:</span>{" "}
                                {attendee.specialRequests}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="px-6 py-3 md:px-8 md:py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            {hasChanges && (
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                You have unsaved changes
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-5 py-2 rounded-xl border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-all duration-200 text-sm"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={`
                px-5 py-2 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 text-sm
                ${
                  hasChanges
                    ? "text-white shadow-lg hover:shadow-xl"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }
              `}
              disabled={!hasChanges || loadingUpdateWaitlist}
              onClick={handleSave}
              onMouseEnter={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor =
                    brandColors.primaryHover;
                }
              }}
              onMouseLeave={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor = brandColors.primary;
                }
              }}
              style={{
                backgroundColor: hasChanges ? brandColors.primary : undefined,
              }}
              type="button"
            >
              {loadingUpdateWaitlist ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default WaitlistModal;
